import * as yup from 'yup';
import { StatusPill } from '../../component/common/status-pill/StatusPill';
import { RowActionsDropdown } from '../../component/common/table/action-dropdown/RowActionsDropdown';

const PLATFORM_OPTIONS = [
  { key: 'Instagram', label: 'Instagram', value: 'Instagram', active: true },
  { key: 'TikTok', label: 'TikTok', value: 'TikTok', active: true },
  { key: 'Facebook', label: 'Facebook', value: 'Facebook', active: true },
  { key: 'YouTube', label: 'YouTube', value: 'YouTube', active: true },
  { key: 'Twitter', label: 'X / Twitter', value: 'Twitter', active: true },
  { key: 'Other', label: 'Other', value: 'Other', active: true }
];

const STATUS_OPTIONS = [
  { key: 'PENDING', label: 'Pending', value: 'PENDING', active: true },
  { key: 'ACTIVE', label: 'Active', value: 'ACTIVE', active: true },
  { key: 'SUSPENDED', label: 'Suspended', value: 'SUSPENDED', active: true }
];

export const getConsultantColumnDefinition = (onEdit, onView, onDelete, onResend, onDownload) => [
  {
    id: 'name',
    header: 'Name',
    size: 180,
    minSize: 140,
    filterFn: 'tableFilterFn',
    accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    cell: ({ row }) => (
      <div>
        <div style={{ fontWeight: 500 }}>{row.original.firstName} {row.original.lastName}</div>
        {row.original.phone && <div style={{ fontSize: '12px', color: 'var(--text-gray)' }}>{row.original.phone}</div>}
      </div>
    )
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: 'Email',
    size: 200,
    minSize: 160,
    filterFn: 'tableFilterFn'
  },
  {
    id: 'social',
    header: 'Social',
    size: 160,
    minSize: 120,
    filterFn: 'tableFilterFn',
    accessorFn: (row) => row.socialHandle ?? '',
    cell: ({ row }) =>
      row.original.socialHandle ? (
        <span className="social-tag">
          <span className="social-tag__platform">{row.original.socialPlatform}</span>
          <span className="social-tag__handle">{row.original.socialHandle}</span>
        </span>
      ) : (
        <span style={{ color: 'var(--text-gray)' }}>-</span>
      )
  },
  {
    id: 'commissionRate',
    accessorKey: 'commissionRate',
    header: 'Commission',
    size: 110,
    minSize: 90,
    cell: ({ row }) =>
      row.original.commissionRate != null
        ? `${row.original.commissionRate}%`
        : <span style={{ color: 'var(--text-gray)' }}>-</span>
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 110,
    minSize: 90,
    cell: ({ row }) => (
      <StatusPill type={row.original.status?.toLowerCase() ?? 'pending'}>
        {row.original.status ?? 'Pending'}
      </StatusPill>
    )
  },
  {
    id: 'signed',
    accessorKey: 'signed',
    header: 'Contract',
    size: 100,
    minSize: 90,
    cell: ({ row }) => (
      <StatusPill type={row.original.signed ? 'signed' : 'unsigned'}>
        {row.original.signed ? 'Signed' : 'Unsigned'}
      </StatusPill>
    )
  },
  {
    id: 'actions',
    header: '',
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    disableExport: true,
    cell: ({ row }) => {
      const c = row.original;
      const actions = [
        onEdit && { label: 'Edit', value: 'edit', onSelect: () => onEdit(row.original) },
        !c.signed && onResend && { label: 'Resend Email', value: 'resend', onSelect: () => onResend(c.id) },
        c.agreementPdfUrl && onDownload && { label: 'Download Agreement', value: 'download', onSelect: () => onDownload(c.id) },
        onDelete && { label: 'Remove', value: 'remove', onSelect: () => onDelete(c) }
      ].filter(Boolean);
      return <RowActionsDropdown row={row} actions={actions} />;
    }
  }
];

export const CONSULTANT_TABS = () => [
  {
    label: 'Details',
    fields: [
      { type: 'input', name: 'firstName', label: 'First Name', required: true,
        validation: yup.string().required('Required') },
      { type: 'input', name: 'lastName', label: 'Last Name', required: true,
        validation: yup.string().required('Required') },
      { type: 'input', name: 'email', label: 'Email', required: true, colSpan: 2,
        validation: yup.string().email('Invalid email').required('Required') },
      { type: 'input', name: 'phone', label: 'Phone',
        validation: yup.string().nullable().optional() },
      { type: 'input', name: 'contractVersion', label: 'Contract Version', defaultValue: '1.0',
        validation: yup.string().nullable().optional() },
      { type: 'input', name: 'socialHandle', label: 'Social Handle',
        validation: yup.string().nullable().optional() },
      {
        type: 'select', name: 'socialPlatform', label: 'Platform',
        options: PLATFORM_OPTIONS,
        validation: yup.string().nullable().optional()
      },
      { type: 'decimal', name: 'commissionRate', label: 'Commission Rate (%)', min: 0, max: 100,
        validation: yup.number().nullable().optional() },
      {
        type: 'select', name: 'status', label: 'Status',
        defaultValue: 'PENDING',
        editOnly: true,
        options: STATUS_OPTIONS,
        validation: yup.string().nullable().optional()
      }
    ]
  },
  {
    label: 'Bank Details',
    fields: [
      { type: 'input', name: 'bankName', label: 'Bank Name', colSpan: 2, validation: yup.string().nullable().optional() },
      { type: 'input', name: 'bankAccountHolder', label: 'Account Holder', colSpan: 2, validation: yup.string().nullable().optional() },
      { type: 'input', name: 'bankAccountNumber', label: 'Account Number', validation: yup.string().nullable().optional() },
      { type: 'input', name: 'bankBranchCode', label: 'Branch Code', validation: yup.string().nullable().optional() }
    ]
  }
];
